import { useState } from 'react';
import { Save as SaveIcon } from '@mui/icons-material';
import { ButtonProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttButton } from './SttButton';
import ModalConfirmation from '../../layout/ModalConfirmation';
import { usePermission } from '../../lib/usePermission';
import { PermissionRequirement } from '../../lib/permissions';

type SttButtonSaveProps = {
    onClick?: () => void | Promise<void>;
    caption?: string;
    confirmation?: boolean;
    /** Permiso v8 que hace falta para ver el botón. Sin él, se ve siempre. */
    permission?: PermissionRequirement;
    loading?: boolean;
} & Omit<ButtonProps, 'onClick'>;

/**
 * Save button
 *
 * Guardar es «la acción que la vista existe para provocar», así que es la
 * variante PRINCIPAL del sistema de diseño: lima de marca con la tinta encima
 * (13,9:1). Antes era `color="success"`, y eso rompía dos reglas a la vez: el
 * verde de confirmación significa «ha salido bien», no «ejecuta esto» — con el
 * mismo verde en las dos cosas el botón parecía un mensaje de éxito — y encima
 * el blanco sobre `#4caf50` se quedaba en 2,78:1.
 */
export function SttButtonSave({
    onClick,
    permission,
    caption,
    confirmation = false,
    ...rest
}: SttButtonSaveProps) {
    const { t } = useTranslation();
    const [confirmationDialog, setConfirmationDialog] = useState({
        show: false,
    });
    const { can } = usePermission();

    if (permission && !can(permission)) return null;

    return (
        <>
            <SttButton
                onClick={
                    confirmation
                        ? () => setConfirmationDialog({ show: true })
                        : onClick
                }
                caption={caption || t('Save')}
                color="primary"
                variant="contained"
                icon={<SaveIcon />}
                {...rest}
            />

            <ModalConfirmation
                show={confirmationDialog.show}
                onClose={() => setConfirmationDialog({ show: false })}
                onAccept={() => {
                    onClick?.();
                    setConfirmationDialog({ show: false });
                }}
            />
        </>
    );
}
